// src/components/DetailItem.tsx
import React from 'react';
import { Box, Typography } from '@mui/material';
import CustomTooltip, { TooltipSection } from './CustomTooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface Props {
  label: string;
  value: string | React.ReactNode;
  valueColor?: string;
  sections?: TooltipSection[];
  icon?: React.ReactNode;
  /** Compact row used inside tooltips. */
  dense?: boolean;
  /** Emphasized row (totals). */
  emphasis?: boolean;
}

/**
 * Label/value row: the label stays quiet, the value carries the weight and
 * aligns right with tabular figures so columns of numbers line up.
 * Rows with details show ⓘ after the label and open them from anywhere on the row.
 */
const DetailItem: React.FC<Props> = ({
  label,
  value,
  valueColor = 'text.primary',
  sections,
  dense = false,
  emphasis = false,
}) => {
  const fontSize = dense ? '0.8125rem' : { xs: '0.8125rem', sm: '0.875rem' };

  const row = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        width: '100%',
        minHeight: dense ? 24 : { xs: 30, md: 32 },
        cursor: sections ? 'help' : undefined,
        // Rows that open details are tap targets on touch: a 44px hit area that doesn't change
        // the row's height (rows stay aligned with their siblings).
        ...(sections &&
          !dense && {
            position: 'relative',
            '@media (pointer: coarse)': {
              '&::after': {
                content: '""',
                position: 'absolute',
                left: 0,
                right: 0,
                top: '50%',
                height: 44,
                transform: 'translateY(-50%)',
              },
            },
          }),
      }}
    >
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
        <Typography
          component="span"
          sx={{
            fontSize,
            fontWeight: emphasis ? 600 : 500,
            color: emphasis ? 'text.primary' : 'text.secondary',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>
        {sections && <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled' }} aria-hidden />}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          flex: 1,
          minWidth: 0,
          gap: 0.75,
          fontSize,
          fontWeight: 600,
          color: valueColor,
          fontVariantNumeric: 'tabular-nums',
          '& a': {
            fontSize,
            fontWeight: 600,
            color: '#E5BF95',
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            // Touch: links get a full 44px row-height target.
            '@media (pointer: coarse)': { minHeight: 44, px: 0.5 },
            '&:hover': { textDecoration: 'underline', textUnderlineOffset: '3px' },
          },
          '& a[target="_blank"]::after': {
            content: '"↗"',
            ml: '2px',
            fontSize: '0.85em',
            opacity: 0.8,
          },
          '& .MuiIconButton-root': {
            p: 0.5,
            color: 'text.secondary',
            '&:hover': { color: 'text.primary' },
          },
        }}
      >
        {typeof value === 'string' ? (
          <Typography
            component="span"
            noWrap
            sx={{ fontSize, fontWeight: emphasis ? 700 : 600, color: valueColor }}
          >
            {value}
          </Typography>
        ) : (
          value
        )}
      </Box>
    </Box>
  );

  if (!sections) return row;

  return (
    <CustomTooltip placement="top" accentColor="secondary.main" sections={sections} fullWidth>
      {row}
    </CustomTooltip>
  );
};

export default DetailItem;
